import React from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';

class ErrorBoundaryClass extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={() => this.setState({ hasError: false, error: null, errorInfo: null })}
          {...this.props}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ 
  error, 
  errorInfo, 
  resetError, 
  title = "Something went wrong",
  message = "An unexpected error occurred. Please try again."
}) => {
  const { theme } = useTheme();

  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  const iconVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        type: "spring",
        stiffness: 200,
        damping: 15,
        delay: 0.2
      }
    }
  };

  return (
    <motion.div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ backgroundColor: theme.colors.backgroundSecondary }}
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <div
        className="max-w-md w-full text-center p-8 rounded-xl"
        style={{ 
          backgroundColor: theme.colors.background,
          boxShadow: theme.shadows.lg
        }}
      >
        <motion.div
          className="text-6xl mb-4"
          variants={iconVariants}
        >
          😵
        </motion.div>

        <motion.h2
          className="text-2xl font-bold mb-2"
          style={{ color: theme.colors.error }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {title}
        </motion.h2>

        <motion.p
          className="mb-6"
          style={{ color: theme.colors.textSecondary }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          {error?.message || message}
        </motion.p>

        <motion.div
          className="space-y-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <motion.button
            className="w-full px-6 py-3 rounded-lg font-medium transition-colors"
            style={{
              backgroundColor: theme.colors.primary,
              color: theme.colors.background
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={resetError}
          >
            Try Again
          </motion.button>

          <motion.button
            className="w-full px-6 py-3 rounded-lg font-medium border transition-colors"
            style={{
              borderColor: theme.colors.border,
              color: theme.colors.textSecondary,
              backgroundColor: 'transparent'
            }}
            whileHover={{ 
              scale: 1.02,
              backgroundColor: theme.colors.surfaceElevated
            }}
            whileTap={{ scale: 0.98 }}
            onClick={() => window.location.reload()}
          >
            Refresh Page
          </motion.button>
        </motion.div>

        {process.env.NODE_ENV === 'development' && error && (
          <motion.details
            className="mt-6 text-left"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <summary 
              className="cursor-pointer text-sm font-medium mb-2"
              style={{ color: theme.colors.textTertiary }}
            >
              Error Details (Development)
            </summary>
            <pre
              className="text-xs p-3 rounded overflow-auto max-h-32"
              style={{
                backgroundColor: theme.colors.backgroundSecondary,
                color: theme.colors.textSecondary
              }}
            >
              {error.stack}
            </pre>
          </motion.details>
        )}
      </div>
    </motion.div>
  );
};

export const ErrorBoundary = (props) => <ErrorBoundaryClass {...props} />;

